<template>
  <iBaseModal
    class="i-resume-builder-modal1"
    use-light-close-icon
    :visible="props.visible"
    :fully-customisable="true"
    max-width="960px"
    @click-on-close="emit('close')"
  >
    <div class="inner">
      <div class="top">
        <img
          class="exclusive"
          :src="ImageExclusive"
        />
        <div class="limited">
          <div>
            <img
              class="clock"
              :src="IconClock"
            />
            <template v-if="isMdOrLarger">Limited-Time Only - </template>Offer Ends In:
          </div>
          <div>
            <div class="timer">{{ props.minutesLeft }} <span class="faded">m</span></div>
            <div class="timer">{{ props.secondsLeft }} <span class="faded">s</span></div>
          </div>
        </div>
      </div>
      <div class="middle">
        <div class="header">
          You've Unlocked
          <span class="badge">$250</span> in FREE ADD-ONS
        </div>
        <div>The Ultimate Job-Ready Resume Package</div>
      </div>
      <div class="bottom">
        <div class="details">
          <div
            v-if="isMdOrLarger"
            class="bar"
          ></div>
          <template
            v-for="(data, i) in details"
            :key="i"
          >
            <div class="column">
              <div class="number">{{ i + 1 }}</div>
              <img
                class="image"
                :src="data.image"
              />
              <div class="title">{{ data.title }}</div>
              <div class="perks">
                <div
                  v-for="(perk, a) in data.perks"
                  :key="a"
                  class="perk"
                >
                  <img
                    class="check"
                    :src="IconCheck"
                  />
                  <div>{{ perk }}</div>
                </div>
              </div>
            </div>
          </template>
        </div>
        <div
          class="action"
          @click="emit('claim-resume-builder')"
        >
          CLAIM FREE GIFT <img :src="IconArrow" />
        </div>
        <div class="keys">
          <div
            v-for="(key, s) in KEY_DATA"
            :key="s"
            class="perk"
          >
            <img
              class="check"
              :src="IconCheck"
            />
            <div>{{ key }}</div>
          </div>
        </div>
      </div>
    </div>
  </iBaseModal>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { breakpointMd } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"

import iBaseModal from "@/components/base/BaseModal.vue"

// ASSETS
import IconArrow from "@/assets/icons/icon-green-right-arrow.svg"
import IconClock from "@/assets/icons/icon-clock-purple.svg"
import IconCheck from "@/assets/icons/icon-check-circle-green.svg"
import ImageExclusive from "@/assets/welcome/rb-exclusive-deal.svg"
import ImageAI from "@/assets/welcome/rb-ai-resume.svg"
import ImageLifetime from "@/assets/welcome/rb-lifetime-access.svg"
import ImageMatch from "@/assets/welcome/rb-job-match.svg"

// EMITS
const emit = defineEmits(["close", "claim-resume-builder"])

// PROPS
const props = defineProps({
  visible: { type: Boolean, required: true },
  minutesLeft: { type: String, required: true },
  secondsLeft: { type: String, required: true }
})

// COMPOSABLES
const isMdOrLarger = useIsScreenSize(breakpointMd)

// CONSTANTS
const KEY_DATA = ["Lifetime Access", "$250 in FREE Add-Ons", "Instant Access"]

// COMPUTED
const details = computed(() => [
  {
    image: ImageAI,
    title: "Instant AI Resume Builder",
    perks: [
      "Create your resume in minutes",
      "AI writing, Powered by ChatGPT-5",
      "Sound instantly more professional",
      ...(isMdOrLarger.value ? ["Get 95% Job Match Rate"] : [])
    ]
  },
  {
    image: ImageLifetime,
    title: "Lifetime Access",
    perks: [
      "Iterate without limits",
      "Unlimited versions & downloads",
      "Edit anytime. Keep it forever",
      "No renewals or Subscription"
    ]
  },
  {
    image: ImageMatch,
    title: "Job Match Optimizer",
    perks: [
      "Tailor your resume to each job",
      "Keyword and ATS alignment",
      "Role-specific optimization",
      "Optimize for ATS screening"
    ]
  }
])
</script>

<style scoped lang="scss">
.i-resume-builder-modal1 {
  .inner {
    @include flex(column, start, stretch, $gap: 16px);

    margin: 30px 20px 20px;

    @media screen and (min-width: $breakpoint-sm) {
      gap: 18px;
    }

    @media screen and (min-width: $breakpoint-md) {
      gap: 20px;
      margin: 45px 35px 35px;
    }

    .top {
      @include border-radius-md;

      margin: auto;
      position: relative;
      border: 2.935px solid $amethyst200;
      background: linear-gradient(90deg, $amethyst50 0%, #fdf2f8 100%);
      padding: 10px 8px;
      min-width: 100%;

      @media screen and (min-width: $breakpoint-sm) {
        padding: 10px 25px;
      }

      @media screen and (min-width: $breakpoint-md) {
        min-width: 85%;
      }

      .exclusive {
        position: absolute;
        top: -56%;
        left: -10px;
        width: 140px;

        @media screen and (min-width: $breakpoint-md) {
          left: -20px;
          width: 23%;
        }
      }

      .limited {
        @include flex(row, center, center, wrap, 5px 10px);
        @include text-sm;

        text-align: center;

        @media screen and (min-width: $breakpoint-sm) {
          @include text-md;
        }

        @media screen and (min-width: $breakpoint-md) {
          @include text-lg;
        }

        > div {
          @include flex(row, center, center, wrap, 10px);
        }

        .clock {
          width: 21px;
        }

        .timer {
          @include border-radius-md;

          color: $amethyst700;
          padding: 0 8px;
          border: 1.467px solid $amethyst200;
          background-color: $white;
          box-shadow:
            0 1.467px 4.402px 0 #0000001a,
            0 1.467px 2.935px -1.467px #0000001a;

          @media screen and (min-width: $breakpoint-md) {
            padding: 0 10px;
          }

          @media screen and (min-width: $breakpoint-lg) {
            padding: 3px 15px;
          }

          .faded {
            @include text-md;

            color: $comet300;
          }
        }
      }
    }

    .middle {
      @include flex(column, start, center, $gap: 8px);
      @include text-xl;

      font-weight: 400;
      text-align: center;

      @media screen and (min-width: $breakpoint-sm) {
        @include text-xxl;

        gap: 14px;
      }

      @media screen and (min-width: $breakpoint-md) {
        @include display-sm;

        font-weight: 300;
        gap: 18px;
      }

      .header {
        @include text-xxl;

        font-weight: 700;

        @media screen and (min-width: $breakpoint-sm) {
          @include display-xs;
        }

        @media screen and (min-width: $breakpoint-md) {
          @include display-md;

          font-weight: 600;
        }

        .badge {
          @include border-radius-xxl;

          background: linear-gradient(90deg, #00c950 0%, #00bc7d 100%);
          box-shadow:
            0 23.864px 35.796px -7.159px #0000001a,
            0 9.546px 14.318px -9.546px #0000001a;
          color: $white;
          padding: 0 12px;

          @media screen and (min-width: $breakpoint-md) {
            padding: 0 20px;
          }
        }
      }
    }

    .bottom {
      @include flex(column, start, center, $gap: 22px);

      .details {
        @include flex(column, start, stretch, $gap: 20px);

        position: relative;

        @media screen and (min-width: $breakpoint-md) {
          @include flex(row, center, stretch, $gap: 10px);

          width: 100%;
        }

        @media screen and (min-width: $breakpoint-lg) {
          @include flex(row, space-between, stretch);
        }

        .bar {
          top: 120px;
          left: 50%;
          transform: translateX(-50%);
          position: absolute;
          width: 80%;
          height: 30px;
          background-color: $astronaut500;
        }

        .column {
          @include flex(column, start, center, $gap: 10px);

          position: relative;
          flex: 0 1 250px;

          @media screen and (min-width: $breakpoint-sm) {
            gap: 14px;
          }

          @media screen and (min-width: $breakpoint-lg) {
            flex: 0 0 290px;
          }

          .number {
            @include flex(row, center, center);
            @include border-radius-rounded;
            @include text-xl;

            left: 0;
            top: 0;
            transform: translateY(-20%);
            position: absolute;
            background-color: $amethyst200;
            width: 48px;
            height: 48px;
            font-weight: 700;
            fill: #ead2fb;
            filter: drop-shadow(0 4px 4px #00000040);

            @media screen and (min-width: $breakpoint-md) {
              display: none;
            }
          }

          .image {
            width: 100%;
            min-width: 220px;
            max-width: 220px;

            @media screen and (min-width: $breakpoint-sm) {
              min-width: 260px;
              max-width: 260px;
            }

            @media screen and (min-width: $breakpoint-md) {
              min-width: 180px;
              max-width: 220px;
            }

            @media screen and (min-width: $breakpoint-lg) {
              min-width: 235px;
              max-width: 235px;
            }
          }

          .title {
            @include text-lg;

            color: #001b74;
            margin-top: 6px;
            text-align: center;

            @media screen and (min-width: $breakpoint-sm) {
              @include text-xxl;
            }

            @media screen and (min-width: $breakpoint-md) {
              @include text-xl;
            }
          }

          .perks {
            @include flex(column, start, start, $gap: 8px);
            @include text-sm;

            @media screen and (min-width: $breakpoint-sm) {
              @include text-lg;
            }

            @media screen and (min-width: $breakpoint-md) {
              @include text-sm;
            }

            @media screen and (min-width: $breakpoint-lg) {
              @include text-md;
            }

            .perk {
              @include flex(row, start, center, $gap: 5px);

              .check {
                width: 16px;

                @media screen and (min-width: $breakpoint-sm) {
                  width: 20px;
                }

                @media screen and (min-width: $breakpoint-md) {
                  width: 16px;
                }

                @media screen and (min-width: $breakpoint-lg) {
                  width: 20px;
                }
              }
            }
          }
        }
      }

      .action {
        @include flex(row, space-between, center, $gap: 10px);
        @include display-xs;
        @include border-radius-xxl;

        width: 100%;
        font-weight: 500;
        color: $white;
        background-color: $bilbao600;
        padding: 10px 15px;
        cursor: pointer;

        @media screen and (min-width: $breakpoint-sm) {
          @include display-sm;

          padding: 10px 30px;
        }

        @media screen and (min-width: $breakpoint-lg) {
          padding: 10px 70px;
        }

        &:hover {
          background-color: $bilbao500;
        }

        img {
          width: 35px;

          @media screen and (min-width: $breakpoint-sm) {
            width: 40px;
          }
        }
      }

      .keys {
        @include flex(column, start, start, $gap: 5px);

        @media screen and (min-width: $breakpoint-sm) {
          @include flex(row, center, stretch, wrap, 5px 30px);
        }

        @media screen and (min-width: $breakpoint-lg) {
          @include flex(row, space-between, stretch);
        }

        .perk {
          @include flex(row, center, center, $gap: 5px);
          @include text-lg;

          flex: 0 0 fit-content;

          @media screen and (min-width: $breakpoint-sm) {
            @include text-xl;
          }

          @media screen and (min-width: $breakpoint-md) {
            @include text-md;
          }

          @media screen and (min-width: $breakpoint-lg) {
            @include text-xl;

            flex: 0 0 290px;
          }

          .check {
            width: 22px;

            @media screen and (min-width: $breakpoint-lg) {
              width: 35px;
            }
          }
        }
      }
    }
  }
}
</style>
